import {Injectable, OnDestroy} from '@angular/core';
import {BaseConfig, BaseConfigService} from './base-config.service';
import {forkJoin, map, mergeMap, Observable, of, ReplaySubject, Subject, take, takeUntil, tap} from 'rxjs';
import {AppConfig, AppFullKey} from '../../app-view/api/v1/types';
import {NGXLogger} from 'ngx-logger';
import {Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';
import {TimeDeviationDialogActions} from '../../modules/dialogs/time-deviation-dialog/+store';
// OpenAPI Shell Service
import {
  ConfigService as ShellApiConfigService,
  ConfigDTO,
  LoggingConfigDTO,
  AuthConfigDTO,
  ShellConfigDTO,
  InfoConfigDTO,
  SentryConfigDTO,
  MatomoConfigDTO,
} from '../../openapi/shell-service-openapi';

export const SUPPORTED_MODES = ['default', 'gewerbeportal'] as const;
export type Mode = typeof SUPPORTED_MODES[number];

// Re-export generierte DTOs für Abwärtskompatibilität (mit alten Namen als Aliase)
export {
  LoggingConfigDTO,
  LoggingConfigDTO as LoggingConfig,
  AuthConfigDTO,
  AuthConfigDTO as AuthConfig,
  ShellConfigDTO,
  ShellConfigDTO as ShellConfig,
  InfoConfigDTO,
  InfoConfigDTO as InfoConfig,
  SentryConfigDTO,
  SentryConfigDTO as SentryConfig,
  MatomoConfigDTO,
  MatomoConfigDTO as MatomoConfig,
} from '../../openapi/shell-service-openapi';


export class Config {

  constructor(
    public mode: Mode,
    public base: BaseConfig,
    public logging: LoggingConfigDTO,
    public auth: AuthConfigDTO,
    public shell: ShellConfigDTO,
    public info: InfoConfigDTO,
    public sentry: SentryConfigDTO,
    public matomo: MatomoConfigDTO,
  ) {
  }
}

export class Apps {

  constructor(
    public appConfigs: AppConfig[],
  ) {
  }

  get appConfigMap(): Map<AppFullKey, AppConfig> {
    return new Map(this.appConfigs.map(app => [app.fullKey, app]));
  }

  get appConfigNavigator(): AppConfig[] {
    return this.appConfigs.filter(app => app.navigator);
  }

  get appConfigEmbedded(): AppConfig[] {
    return this.appConfigs.filter(app => app.embedded);
  }

  get appConfigEmbeddedMap(): Map<AppFullKey, AppConfig> {
    return new Map(this.appConfigEmbedded.map(app => [app.fullKey, app]));
  }
}


@Injectable()
export class ConfigService
  implements OnDestroy {

  private readonly unsubscribe$ = new Subject<void>();

  private readonly loaded = new ReplaySubject<Config>();

  private readonly DEVIATION_COUNT_KEY = 'shell.time-deviation.count';

  private config_?: Config;

  private shellModeOverride?: Mode;

  private backendMode?: Mode;

  private serverConfigCache?: ConfigDTO;

  constructor(
    private store: Store,
    private actions$: Actions,
    private logger: NGXLogger,
    private baseConfigService: BaseConfigService,
    private shellApiConfigService: ShellApiConfigService,
  ) {
    this.checkQueryParameterOverride();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    this.loaded.complete();
  }

  get loaded$() {
    return this.loaded.pipe(
      takeUntil(this.unsubscribe$),
    );
  }

  get config(): Config {
    return this.config_ as Config;
  }

  loadConfig() {
    return forkJoin([
      this.getConfig(),
    ]);
  }

  private getConfig(): Observable<Config> {
    return this.baseConfigService.loaded$
      .pipe(
        takeUntil(this.unsubscribe$),
        take(1),
        tap(baseConfig => {
          // INFO: basePath setzen, bevor der API-Call gemacht wird
          // Der generierte Service hat einen hardcoded Default, den wir hier überschreiben
          this.shellApiConfigService.configuration.basePath = baseConfig.baseUrl;
        }),
        mergeMap(() => this.shellApiConfigService.readConfig()),
        mergeMap(value => this.checkTimeDeviation(value)),
        tap(value => {
          // info: cache server config for later use (e.g. mode override)
          this.serverConfigCache = value;

          // info: extract backend mode
          this.backendMode = this.extractBackendMode(value);
          this.logger.debug(`ConfigService: Backend mode determined: ${this.backendMode}`);
        }),
        map(value => this.createConfigFromServerData(value)),
        tap(value => {
          this.config_ = value;
          this.loaded.next(this.config_);
        }),
      );
  }

  private extractBackendMode(serverConfig: ConfigDTO): Mode {
    if (serverConfig.mode && SUPPORTED_MODES.includes(serverConfig.mode as Mode)) {
      return serverConfig.mode as Mode;
    }
    return 'default';
  }

  private determineEffectiveMode(): Mode {
    if (this.shellModeOverride) {
      this.logger.debug(`ConfigService: Using mode override: ${this.shellModeOverride}`);
      return this.shellModeOverride;
    }

    const mode = this.backendMode || 'default';
    this.logger.debug(`ConfigService: Using backend mode: ${mode}`);
    return mode;
  }

  private createConfigFromServerData(serverConfig: ConfigDTO): Config {
    const effectiveMode = this.determineEffectiveMode();

    return new Config(
      effectiveMode,
      this.baseConfigService.baseConfig,
      serverConfig.logging,
      serverConfig.auth,
      serverConfig.shell,
      serverConfig.info,
      serverConfig.sentry,
      serverConfig.matomo,
    );
  }

  private checkTimeDeviation(
    config: ConfigDTO,
  ): Observable<ConfigDTO> {
    const timeDeviation = this.baseConfigService.baseConfig?.timeDeviation;
    if (!timeDeviation) {
      // info: time deviation not present
      return of(config);
    }
    const timeDeviationConfig = config.shell?.timeDeviation;
    if (!timeDeviationConfig) {
      // info: time deviation check configuration not present
      return of(config);
    }

    const deviationCount = this.getDeviationCount();

    const minErrorOccurrences = timeDeviationConfig.minOccurrences ?? 5;
    const minError = timeDeviationConfig.minError;
    if (
      deviationCount >= minErrorOccurrences
      && minError && Math.abs(timeDeviation) > Math.abs(minError)
    ) {
      this.setDeviationCount(0);
      this.logger.error('ConfigService: Time deviation is too high: ' + timeDeviation + ' ms.');

      // info: show dialog
      this.store.dispatch(TimeDeviationDialogActions.open());
      return this.actions$.pipe(
        ofType(TimeDeviationDialogActions.close),
        take(1),
        map(() => config),
      );
    }

    // info: a minWarn threshold is required when minError is set to ensure the counter is incremented
    const minWarn = timeDeviationConfig.minWarn ?? timeDeviationConfig.minError;
    if (minWarn && Math.abs(timeDeviation) > Math.abs(minWarn)) {
      this.logger.warn('ConfigService: Time deviation is high: ' + timeDeviation + ' ms, this may cause problems!');
      this.setDeviationCount(deviationCount + 1);

      return of(config);
    }

    return of(config);
  }

  private getDeviationCount(): number {
    try {
      return Number(sessionStorage.getItem(this.DEVIATION_COUNT_KEY)) || 0;
    } catch (e) {
      return Number.MAX_VALUE;
    }
  }

  private setDeviationCount(value: number): number {
    try {
      sessionStorage.setItem(this.DEVIATION_COUNT_KEY, value.toString());
      return value;
    } catch (e) {
      return Number.MAX_VALUE;
    }
  }

  private checkQueryParameterOverride(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const shellModeParam = urlParams.get('shellMode');

    if (shellModeParam && SUPPORTED_MODES.includes(shellModeParam as Mode)) {
      this.shellModeOverride = shellModeParam as Mode;
      this.logger.info(`ConfigService: Using shellMode override from query parameter: ${shellModeParam}`);
    }
  }

  public setModeOverride(mode: Mode | undefined): void {
    if (mode && !SUPPORTED_MODES.includes(mode)) {
      this.logger.warn(`ConfigService: Invalid mode '${mode}' provided to setModeOverride`);
      return;
    }

    this.shellModeOverride = mode;

    if (!this.serverConfigCache) {
      this.logger.warn('ConfigService: Cannot set mode override before initial config is loaded');
      return;
    }

    const newConfig = this.createConfigFromServerData(this.serverConfigCache);

    this.config_ = newConfig;
    this.loaded.next(newConfig);

    const effectiveMode = this.determineEffectiveMode();
    this.logger.info(`ConfigService: Mode override set to: ${mode || 'none (using backend value)'}, effective mode: ${effectiveMode}`);
  }

  public getModeOverride(): Mode | undefined {
    return this.shellModeOverride;
  }
}
